import { useLayoutEffect, useRef, useState } from "react";

import type { LegacyMatch, PlayerSummary, TournamentResponse } from "../api/legacyTypes";
import { slotFeederHint } from "../utils/bracketFeeders";
import { entitySlug } from "../utils/slug";
import { CountryMark } from "./CountryMark";
import { EntityLink } from "./EntityLink";
import { EntityLogo } from "./EntityLogo";
import { FeederPlaceholder } from "./FeederPlaceholder";
import { LocalDateTime } from "./LocalDateTime";

type Bracket = TournamentResponse["tournament"]["brackets"][number];

interface Connector {
  key: string;
  path: string;
  sourceParticipants: string[];
  targetParticipants: string[];
  routeOnly: boolean;
  sourceKey: string;
  targetKey: string;
  targetSlot: number;
}

interface FeederHint {
  targetKey: string;
  targetRound: string;
  slot: number;
  sourceKeys: string[];
}

interface BracketResultState {
  hidden: (match: LegacyMatch) => boolean;
  reveal: (match: LegacyMatch) => void;
}

function participants(match: LegacyMatch) {
  return [match.participant1, match.participant2, match.participant3, match.participant4].filter(Boolean);
}

function outcome(match: LegacyMatch, slot: 1 | 2 | 3 | 4) {
  if (match.status !== "completed") return "";
  const scores = [match.score1, match.score2, match.score3, match.score4].map((score) => Number(score)).filter(Number.isFinite);
  if (scores.length < 2) return "";
  const score = Number(match[`score${slot}`]);
  if (!Number.isFinite(score)) return "";
  const ranked = [...scores].sort((left, right) => right - left);
  const advances = match.participant3 || match.participant4 ? score >= ranked[Math.min(1, ranked.length - 1)] : score === ranked[0];
  return advances ? "participant-advanced" : "participant-eliminated";
}

function visualStatus(match: LegacyMatch) {
  if (match.status === "ongoing") return "ongoing";
  const hasNamedParticipant = [match.participant1, match.participant2, match.participant3, match.participant4].some((name) => name.trim() !== "");
  const hasLiveScore = match.status === "scheduled" && hasNamedParticipant && [match.score1, match.score2, match.score3, match.score4].some((score) => score !== "");
  return hasLiveScore ? "ongoing" : match.status;
}

function matchStageLabel(match: LegacyMatch, roundName: string) {
  const round = match.round.trim();
  if (round && round.toLowerCase() !== roundName.trim().toLowerCase()) return round;
  const group = match.group.trim();
  return group.toLowerCase() === roundName.trim().toLowerCase() ? "" : group;
}

function qualificationSlots(match: LegacyMatch) {
  if (match.status !== "completed") return ["", ""];
  const candidates = [1, 2, 3, 4].map((slot) => ({
    name: match[`participant${slot}` as keyof LegacyMatch] as string,
    score: Number.parseFloat(String(match[`score${slot}` as keyof LegacyMatch]).replace("%", "")),
  }));
  if (candidates.some(({ name, score }) => !name || !Number.isFinite(score))) return ["", ""];
  candidates.sort((left, right) => right.score - left.score);
  return [candidates[0].name, candidates[1].score === candidates[2].score ? "" : candidates[1].name];
}

function sourceBracketRound(match: LegacyMatch) {
  const parsed = match.sourceKey.match(/\/r(\d+)m\d+$/i);
  return parsed ? Number(parsed[1]) : null;
}

function splitTrackmaniaHybridBracket(bracket: Bracket) {
  const winnersFinalIndex = bracket.rounds.findIndex((round) => round.name.trim().toLowerCase() === "winners final");
  const losersFinalIndex = bracket.rounds.findIndex((round) => round.name.trim().toLowerCase() === "losers final");
  const consolidationFinalIndex = bracket.rounds.findIndex((round) => round.name.trim().toLowerCase() === "consolidation final");
  if (winnersFinalIndex < 1 || losersFinalIndex <= winnersFinalIndex || consolidationFinalIndex <= losersFinalIndex) return null;

  const upper: Bracket["rounds"] = [];
  const lower: Bracket["rounds"] = [];
  const final: Bracket["rounds"] = [];
  bracket.rounds.forEach((round, roundIndex) => {
    if (roundIndex > consolidationFinalIndex) {
      final.push({ ...round, name: round.name.toLowerCase().includes("final") ? round.name : "Grand Final" });
      return;
    }
    if (roundIndex === winnersFinalIndex) {
      upper.push(round);
      return;
    }
    if (roundIndex > winnersFinalIndex) {
      lower.push(round);
      return;
    }
    if (roundIndex === 0) {
      upper.push(round);
      return;
    }

    const indexedMatches = round.matches.map((match) => ({ match, sourceRound: sourceBracketRound(match) }));
    const sourceRounds = indexedMatches.map(({ sourceRound }) => sourceRound).filter((value): value is number => value !== null);
    const upperSourceRound = sourceRounds.length ? Math.max(...sourceRounds) : null;
    const upperMatches = indexedMatches.filter(({ sourceRound }) => sourceRound === upperSourceRound).map(({ match }) => match);
    const lowerMatches = indexedMatches.filter(({ sourceRound }) => sourceRound !== upperSourceRound).map(({ match }) => match);
    if (upperMatches.length) upper.push({ ...round, matches: upperMatches });
    if (lowerMatches.length) lower.push({ ...round, matches: lowerMatches });
  });

  return upper.length && lower.length ? { upper, lower, final } : null;
}

function QualificationCard({ match, playerSummaries, resultState }: { match: LegacyMatch; playerSummaries: Record<string, PlayerSummary>; resultState?: BracketResultState }) {
  const hidden = resultState?.hidden(match) ?? false;
  return <article className={`qualification-card ${hidden ? "qualification-hidden" : ""}`}>
    <div className="qualification-meta"><strong>Top 2 by points</strong><small>from {match.sourceKey.split("/").at(-1)?.toUpperCase()}</small></div>
    {qualificationSlots(match).map((name, index) => {
      const summary = name ? playerSummaries[`trackmania/${entitySlug(name)}`] : undefined;
      return <div className={`qualification-slot ${name ? "" : "tbd"}`} key={`${match.sourceKey}:${index}`}>
        <span className="bracket-participant-identity">
          {name ? <>{summary?.country && <CountryMark country={summary.country} />}<EntityLink gameId="trackmania" kind="player" name={name} /></> : <span>TBD</span>}
        </span>
        {summary?.organization && <a className="bracket-organization" href={`/organizations/${summary.organization.id}`} title={summary.organization.name}><EntityLogo src={summary.organization.logo_url || `/media/organization/${summary.organization.id}`} name={summary.organization.name} /></a>}
        <small>Qualified player</small>
      </div>;
    })}
  </article>;
}

function participant(match: LegacyMatch, wiki: string, slot: 1 | 2 | 3 | 4, allMatches: LegacyMatch[], active: string, setActive: (name: string) => void, downstreamHidden: boolean, playerSummaries: Record<string, PlayerSummary>, feederHint: FeederHint | null, setFeederHint: (hint: FeederHint | null) => void) {
  const rawName = match[`participant${slot}`];
  const expectedEmptySlot = match.status !== "completed" && (wiki === "trackmania" || slot <= 2);
  const name = rawName || (expectedEmptySlot ? "TBD" : "");
  const score = match[`score${slot}`];
  if (!name) return null;
  const isTBD = name.trim().toLowerCase() === "tbd";
  const teamId = slot === 1 ? match.teamId1 : slot === 2 ? match.teamId2 : "";
  const logo = slot === 1 ? match.logo1 : slot === 2 ? match.logo2 : "";
	const country = logo.startsWith("country:") ? logo.slice("country:".length) : "";
  const individual = wiki === "trackmania" || ((wiki === "chess" || wiki === "ageofempires" || wiki === "osu") && !teamId);
  const playerSummary = individual ? playerSummaries[`${wiki}/${entitySlug(name)}`] : undefined;
  const feederActive = isTBD && feederHint?.targetKey === match.sourceKey && feederHint.slot === slot;
  const displayHint = isTBD ? slotFeederHint(match, slot, wiki, allMatches) : undefined;
  const activateFeeder = () => setFeederHint({ targetKey: match.sourceKey, targetRound: match.round, slot, sourceKeys: displayHint?.sourceKeys ?? [] });
  return (
    <div key={slot} className={`bracket-participant ${outcome(match, slot)} ${active === name ? "participant-path-active" : ""} ${downstreamHidden ? "participant-downstream-hidden" : ""} ${feederActive ? "participant-feeder-target" : ""}`} onMouseEnter={() => {
      if (isTBD) activateFeeder();
      else if (!downstreamHidden) setActive(name);
    }} onMouseLeave={() => { setActive(""); if (isTBD) setFeederHint(null); }}>
      <span className="bracket-participant-identity">
        {isTBD ? <span className="bracket-tbd" title="Hover to trace possible feeder matches" tabIndex={0} onFocus={activateFeeder} onBlur={() => setFeederHint(null)}><FeederPlaceholder gameId={wiki} hint={displayHint} /></span> : <>
          {(playerSummary?.country || country) && <CountryMark country={playerSummary?.country || country} />}
          <EntityLink gameId={wiki} kind={individual ? "player" : "team"} name={name} entityId={teamId} logoUrl={country ? "" : teamId ? `/media/organization/${teamId}` : logo} showLogo={!country} />
        </>}
      </span>
      {playerSummary?.organization && <a className="bracket-organization" href={`/organizations/${playerSummary.organization.id}`} title={playerSummary.organization.name}><EntityLogo src={playerSummary.organization.logo_url || `/media/organization/${playerSummary.organization.id}`} name={playerSummary.organization.name} /></a>}
      {score && <strong>{score}</strong>}
    </div>
  );
}

function Board({ bracket, wiki, tournamentId, allMatches, resultState, playerSummaries, feederHint, setFeederHint, externalFeederKeys }: { bracket: Bracket; wiki: string; tournamentId?: string; allMatches: LegacyMatch[]; resultState?: BracketResultState; playerSummaries: Record<string, PlayerSummary>; feederHint: FeederHint | null; setFeederHint: (hint: FeederHint | null) => void; externalFeederKeys: Set<string> }) {
  const boardRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef(new Map<string, HTMLElement>());
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [active, setActive] = useState("");
  useLayoutEffect(() => {
    const draw = () => {
      const board = boardRef.current;
      if (!board) return;
      const boardRect = board.getBoundingClientRect();
      const next: Connector[] = [];
      for (let roundIndex = 0; roundIndex < bracket.rounds.length - 1; roundIndex += 1) {
        const sources = bracket.rounds[roundIndex].matches;
        const targets = bracket.rounds[roundIndex + 1].matches;
        if (!sources.length || !targets.length) continue;
        const pairs = new Map<string, { source: number; target: number; routeOnly: boolean }>();
        sources.forEach((_, source) => {
          const target = Math.min(targets.length - 1, Math.floor(source * targets.length / sources.length));
          pairs.set(`${source}:${target}`, { source, target, routeOnly: false });
        });
        sources.forEach((sourceMatch, source) => targets.forEach((targetMatch, target) => {
          const sourceNames = participants(sourceMatch);
          if (sourceNames.some((name) => participants(targetMatch).includes(name)) && !pairs.has(`${source}:${target}`)) {
            pairs.set(`${source}:${target}`, { source, target, routeOnly: true });
          }
        }));
        const pairValues = [...pairs.values()];
        const targetSlots = new Map<string, number>();
        for (const pair of pairValues) {
          const sourceNames = participants(sources[pair.source]);
          const targetNames = participants(targets[pair.target]);
          const matchingSlot = targetNames.findIndex((name) => name.toLowerCase() !== "tbd" && sourceNames.includes(name));
          if (matchingSlot >= 0) targetSlots.set(`${pair.source}:${pair.target}`, matchingSlot + 1);
        }
        for (const pair of pairValues) {
          const pairKey = `${pair.source}:${pair.target}`;
          if (targetSlots.has(pairKey)) continue;
          const claimed = new Set(pairValues.filter((other) => other.target === pair.target).map((other) => targetSlots.get(`${other.source}:${other.target}`)).filter((slot): slot is number => Boolean(slot)));
          const targetSlot = [1, 2, 3, 4].find((slot) => !claimed.has(slot)) ?? 1;
          targetSlots.set(pairKey, targetSlot);
        }
        for (const pair of pairValues) {
          const sourceKey = `${roundIndex}:${pair.source}`;
          const targetKey = `${roundIndex + 1}:${pair.target}`;
          const sourceNode = nodeRefs.current.get(sourceKey);
          const targetNode = nodeRefs.current.get(targetKey);
          if (!sourceNode || !targetNode) continue;
          const sourceRect = sourceNode.getBoundingClientRect();
          const targetRect = targetNode.getBoundingClientRect();
          const startX = sourceRect.right - boardRect.left;
          const startY = sourceRect.top + sourceRect.height / 2 - boardRect.top;
          const endX = targetRect.left - boardRect.left;
          const endY = targetRect.top + targetRect.height / 2 - boardRect.top;
          const middleX = startX + (endX - startX) / 2;
          next.push({ key: `${roundIndex}:${pair.source}:${pair.target}`, path: `M ${startX} ${startY} H ${middleX} V ${endY} H ${endX}`, sourceParticipants: participants(sources[pair.source]), targetParticipants: participants(targets[pair.target]), routeOnly: pair.routeOnly, sourceKey: sources[pair.source].sourceKey, targetKey: targets[pair.target].sourceKey, targetSlot: targetSlots.get(`${pair.source}:${pair.target}`) ?? 1 });
        }
      }
      setConnectors(next);
    };
    const frame = window.requestAnimationFrame(draw);
    const observer = new ResizeObserver(draw);
    if (boardRef.current) observer.observe(boardRef.current);
    window.addEventListener("resize", draw);
    return () => { window.cancelAnimationFrame(frame); observer.disconnect(); window.removeEventListener("resize", draw); };
  }, [bracket]);
  return (
    <div className={`bracket-board bracket-board-${wiki}`} ref={boardRef}>
      <svg className="bracket-connectors" aria-hidden="true">{connectors.map((connector) => {
        const sourceMatch = allMatches.find((match) => match.sourceKey === connector.sourceKey);
        const targetMatch = allMatches.find((match) => match.sourceKey === connector.targetKey);
        const safe = (!sourceMatch || !resultState?.hidden(sourceMatch)) && (!targetMatch || !resultState?.hidden(targetMatch));
        const highlighted = safe && active && connector.sourceParticipants.includes(active) && connector.targetParticipants.includes(active);
        const feederActive = safe && feederHint?.targetKey === connector.targetKey && feederHint.slot === connector.targetSlot;
        return <path key={connector.key} d={connector.path} className={`${connector.routeOnly ? "connector-route-only" : ""} ${highlighted ? "connector-path-active" : ""} ${feederActive ? "connector-feeder-active" : ""}`} />;
      })}</svg>
      {bracket.rounds.map((round, roundIndex) => (
        <section className="bracket-round" key={round.name}>
          <h4>{round.name}</h4>
          <div className="bracket-round-matches">
            {round.matches.map((match, matchIndex) => {
              const hidden = resultState?.hidden(match) ?? false;
              const status = visualStatus(match);
              const feederSource = externalFeederKeys.has(match.sourceKey) || connectors.some((connector) => feederHint?.targetKey === connector.targetKey && feederHint.slot === connector.targetSlot && connector.sourceKey === match.sourceKey);
              const stageLabel = matchStageLabel(match, round.name);
              return <article ref={(node) => { const key = `${roundIndex}:${matchIndex}`; if (node) nodeRefs.current.set(key, node); else nodeRefs.current.delete(key); }} className={`bracket-match status-border-${status} ${active && !hidden && participants(match).includes(active) ? "bracket-node-path-active" : ""} ${feederSource ? "bracket-node-feeder-active" : ""} ${hidden ? "bracket-result-hidden" : ""}`} key={match.sourceKey}>
                <div className="bracket-match-meta">{stageLabel && <span>{stageLabel}</span>}<span className={status === "ongoing" ? "match-status-live" : ""}>{status === "ongoing" ? "Live" : status}</span></div>
                {[1, 2, 3, 4].map((slot) => {
                  const name = match[`participant${slot}` as keyof LegacyMatch] as string;
                  const downstreamHidden = Boolean(name && allMatches.some((source) => source.sortOrder < match.sortOrder && resultState?.hidden(source) && participants(source).includes(name)));
                  return participant(match, wiki, slot as 1 | 2 | 3 | 4, allMatches, active, setActive, downstreamHidden, playerSummaries, feederHint, setFeederHint);
                })}
                {match.startsAt && <LocalDateTime value={match.startsAt} status={match.status} />}
                {(wiki === "dota2" || wiki === "counterstrike") && tournamentId && <a className="bracket-match-stats" href={`/tournaments/${wiki}/${tournamentId.split("/").at(-1)}/matches/${encodeURIComponent(match.sourceKey)}`}>Stats</a>}
                {hidden && <button className="bracket-reveal" type="button" onClick={() => resultState?.reveal(match)}>Reveal</button>}
              </article>
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function TrackmaniaGroupBoard({ bracket, allMatches, resultState, playerSummaries, feederHint, setFeederHint }: { bracket: Bracket; allMatches: LegacyMatch[]; resultState?: BracketResultState; playerSummaries: Record<string, PlayerSummary>; feederHint: FeederHint | null; setFeederHint: (hint: FeederHint | null) => void }) {
  const upperSource = bracket.rounds[1];
  const lowerSource = bracket.rounds[3];
  const lanes = [
    { key: "upper", title: "Upper bracket", description: "Win through two heats to qualify", rounds: [{ ...bracket.rounds[0], name: "UB Quarterfinals" }, { ...upperSource, name: "UB Semifinals" }], source: upperSource },
    { key: "lower", title: "Lower bracket", description: "Last route into the playoffs", rounds: [{ ...bracket.rounds[2], name: "LB Quarterfinals" }, { ...lowerSource, name: "LB Semifinals" }], source: lowerSource },
  ];
  return <div className="tm-group-board">
    {lanes.map((lane) => <section className={`tm-qualification-lane tm-${lane.key}-lane`} key={lane.key}>
      <div className="tm-lane-heading"><strong>{lane.title}</strong><span>{lane.description}</span></div>
      <div className="tm-lane-track">
        <Board bracket={{ name: lane.title, rounds: lane.rounds }} wiki="trackmania" allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={new Set()} />
        <section className="tm-qualification-column">
          <h4>Qualified</h4>
          <div className="tm-qualification-cards">{lane.source.matches.map((match) => <QualificationCard match={match} playerSummaries={playerSummaries} resultState={resultState} key={`qualified:${match.sourceKey}`} />)}</div>
        </section>
      </div>
    </section>)}
  </div>;
}

function BracketContents({ bracket, wiki, tournamentId, allMatches, resultState, playerSummaries }: { bracket: Bracket; wiki: string; tournamentId?: string; allMatches: LegacyMatch[]; resultState?: BracketResultState; playerSummaries: Record<string, PlayerSummary> }) {
  const [feederHint, setFeederHint] = useState<FeederHint | null>(null);
  if (wiki === "trackmania" && bracket.name.toLowerCase().startsWith("group ") && bracket.rounds.length >= 4) {
    return <TrackmaniaGroupBoard bracket={bracket} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} />;
  }
  const hybridTrackmania = wiki === "trackmania" ? splitTrackmaniaHybridBracket(bracket) : null;
  const lower = hybridTrackmania?.lower ?? bracket.rounds.filter((round) => /^(lower bracket|lb(?:\s|$))/.test(round.name.trim().toLowerCase()));
  const final = hybridTrackmania?.final ?? bracket.rounds.filter((round) => round.name.toLowerCase().includes("grand final"));
  let upper = hybridTrackmania?.upper ?? bracket.rounds.filter((round) => /^(upper bracket|ub(?:\s|$))/.test(round.name.trim().toLowerCase()));
  // Some Liquipedia Trackmania brackets abbreviate only the lower lane (for
  // example "LB Round of 24") and leave the upper lane as "Groups" or
  // numbered rounds. Once a lower lane is explicit, every non-lower,
  // non-grand-final progression round belongs to the upper lane.
  if (lower.length > 0 && upper.length === 0) {
    upper = bracket.rounds.filter((round) => !lower.includes(round) && !final.includes(round));
  }
  const externalFeederKeys = new Set<string>(feederHint?.sourceKeys ?? []);
  if (feederHint) {
    const targetRound = feederHint.targetRound.toLowerCase();
    let sourceRound = "";
    if (targetRound.includes("lower bracket quarterfinal")) sourceRound = "upper bracket semifinals";
    else if (targetRound.includes("lower bracket final") && feederHint.slot === 2) sourceRound = "upper bracket final";
    else if (targetRound.includes("grand final")) sourceRound = feederHint.slot === 1 ? "upper bracket final" : "lower bracket final";
    if (sourceRound && externalFeederKeys.size === 0) bracket.rounds.filter((round) => round.name.toLowerCase() === sourceRound).forEach((round) => round.matches.forEach((match) => externalFeederKeys.add(match.sourceKey)));
  }
  if (upper.length && lower.length) {
    return <div className={`double-elimination-board ${final.length ? "" : "double-elimination-no-final"}`}>
      <section className="double-elimination-lane upper-bracket-lane"><h4>Upper bracket</h4><Board bracket={{ name: "Upper bracket", rounds: upper }} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={externalFeederKeys} /></section>
      <section className="double-elimination-lane lower-bracket-lane"><h4>Lower bracket</h4><Board bracket={{ name: "Lower bracket", rounds: lower }} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={externalFeederKeys} /></section>
      {final.length > 0 && <section className="double-elimination-final" aria-label="Grand final"><Board bracket={{ name: "Grand final", rounds: final }} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={externalFeederKeys} /></section>}
    </div>;
  }
  const placement = bracket.rounds.filter((round) => /third place|3rd place|bronze|2nd chance|second chance/.test(round.name.toLowerCase()));
  const progression = bracket.rounds.filter((round) => !placement.includes(round));
  if (placement.length && progression.length) {
    const columnStep = wiki === "trackmania" ? 254 : wiki === "dota2" ? 218 : 223;
    return <div className="single-elimination-with-placement">
      <div className="single-elimination-main"><Board bracket={{ ...bracket, rounds: progression }} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={externalFeederKeys} /></div>
      <aside className="single-elimination-placement" aria-label="Placement and qualification matches" style={{ paddingLeft: `${(progression.length - 1) * columnStep}px` }}><Board bracket={{ name: "Placement and qualification matches", rounds: placement }} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={externalFeederKeys} /></aside>
    </div>;
  }
  return <Board bracket={bracket} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} feederHint={feederHint} setFeederHint={setFeederHint} externalFeederKeys={externalFeederKeys} />;
}

export function BracketBoard({ bracket, wiki, tournamentId, allMatches, resultState, playerSummaries = {} }: { bracket: Bracket; wiki: string; tournamentId?: string; allMatches: LegacyMatch[]; resultState?: BracketResultState; playerSummaries?: Record<string, PlayerSummary> }) {
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  return (
    <div className="bracket-widget">
      <div className="bracket-widget-heading"><h3>{bracket.name}</h3><button className="button-secondary" type="button" onClick={() => setExpanded(true)}>Expand</button></div>
      <div className="bracket-scroll"><BracketContents bracket={bracket} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} /></div>
      {expanded && (
        <div className="bracket-modal" role="dialog" aria-modal="true" aria-label={`${bracket.name} expanded`}>
          <header><strong>{bracket.name}</strong><div><button type="button" onClick={() => setZoom(Math.max(.65, zoom - .1))}>−</button><span>{Math.round(zoom * 100)}%</span><button type="button" onClick={() => setZoom(Math.min(1.8, zoom + .1))}>+</button><button type="button" onClick={() => setZoom(1)}>Reset</button><button type="button" onClick={() => setExpanded(false)}>Close</button></div></header>
          <div className="bracket-modal-viewport"><div style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}><BracketContents bracket={bracket} wiki={wiki} tournamentId={tournamentId} allMatches={allMatches} resultState={resultState} playerSummaries={playerSummaries} /></div></div>
        </div>
      )}
    </div>
  );
}
